var _excluded = ["sx", "css"],
    _excluded2 = ["isClearable", "isSearchable", "leftIcon", "rightIcon", "isFocused"],
    _excluded3 = ["hidden", "placeholder"],
    _excluded4 = ["rootProps"],
    _excluded5 = ["handleOptionClick", "options", "width"];

function _objectDestructuringEmpty(obj) { if (obj == null) throw new TypeError("Cannot destructure undefined"); }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

/* eslint-disable max-len */
import { Icon } from '@chakra-ui/icon';
import { chakra, forwardRef, omitThemingProps, StylesProvider, useMultiStyleConfig, useStyles } from '@chakra-ui/system';
import { __DEV__, cx } from '@chakra-ui/utils';
import { motion } from 'framer-motion';
import * as React from 'react';
import { CaretDownIcon, CaretUpIcon, CloseIcon } from './icons';
import { SelectDescendantsProvider, SelectProvider, useSelect, useSelectContext, useSelectInputValue, useSelectList, useSelectOption, useSelectPositioner, useSelectValueContainer } from './use-select';
import { getIconStyles } from './utils';

/**
 * Select provides context, state, and focus management
 */
export var Select = forwardRef((props, ref) => {
  var ownProps = omitThemingProps(props);

  var {
    sx,
    css
  } = ownProps,
      rest = _objectWithoutPropertiesLoose(ownProps, _excluded);

  var styles = useMultiStyleConfig('Select', props);
  var ctx = useSelect(rest);
  var context = React.useMemo(() => ctx, [ctx]);
  var {
    descendants
  } = context;
  return /*#__PURE__*/React.createElement(SelectDescendantsProvider, {
    value: descendants
  }, /*#__PURE__*/React.createElement(SelectProvider, {
    value: context
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(chakra.div, {
    css: css,
    "data-test-id": props['data-test-id'],
    ref: ref,
    sx: sx
  }, /*#__PURE__*/React.createElement(SelectContainer, null), /*#__PURE__*/React.createElement(SelectList, null)))));
});

if (__DEV__) {
  Select.displayName = 'Select';
}

/**
 * The main wrapper to hold the value for the selected component. 
 * Must be a direct child of `Select`.
 * Triggers SelectList and handles search.
 */
export var SelectContainer = forwardRef((props, ref) => {
  var rest = _extends({}, (_objectDestructuringEmpty(props), props));

  var context = useSelectValueContainer(rest, ref);
  var {
    containerProps,
    clearProps
  } = context;

  var {
    isClearable,
    isSearchable,
    leftIcon,
    rightIcon
  } = containerProps,
      containerRest = _objectWithoutPropertiesLoose(containerProps, _excluded2);

  var CaretIcon = containerProps['aria-expanded'] ? CaretUpIcon : CaretDownIcon;
  var clearable = isSearchable && clearProps.value && isClearable;
  var styles = useStyles();
  var leftStyles = {
    display: 'flex',
    alignItems: 'center'
  };
  var rightStyles = {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'end'
  };
  return /*#__PURE__*/React.createElement(chakra.div, _extends({
    __css: styles.container,
    className: cx('chakra-select__select-container', props.className)
  }, containerRest), /*#__PURE__*/React.createElement(chakra.div, {
    __css: leftStyles
  }, (leftIcon == null ? void 0 : leftIcon.icon) && /*#__PURE__*/React.createElement(Icon, _extends({}, getIconStyles(leftIcon), {
    as: leftIcon == null ? void 0 : leftIcon.icon,
    mr: "0.8rem"
  })), /*#__PURE__*/React.createElement(SelectInputValue, null)), /*#__PURE__*/React.createElement(chakra.div, {
    __css: rightStyles
  }, clearable && /*#__PURE__*/React.createElement(CloseIcon, clearProps), rightIcon ? /*#__PURE__*/React.createElement(Icon, _extends({}, getIconStyles(rightIcon), {
    as: rightIcon == null ? void 0 : rightIcon.icon,
    ml: "0.8rem"
  })) : /*#__PURE__*/React.createElement(Icon, {
    __css: styles.icon,
    as: CaretIcon
  })));
});

if (__DEV__) {
  SelectContainer.displayName = 'SelectContainer';
}

export var SelectInputValue = forwardRef((props, ref) => {
  var valueInputCtx = useSelectInputValue(props, ref);
  var {
    getInputProps,
    getValueProps
  } = valueInputCtx;
  var {
    children
  } = getValueProps;

  var {
    hidden,
    placeholder
  } = getInputProps,
      inputRest = _objectWithoutPropertiesLoose(getInputProps, _excluded3);

  var styles = useStyles();

  var inputStyles = _extends({}, styles.input);

  var divStyles = {
    width: '100%',
    display: 'flex'
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(chakra.span, _extends({
    __css: inputStyles,
    className: cx('chakra-select__select-span-value', props.className)
  }, getValueProps), /*#__PURE__*/React.createElement(chakra.div, {
    __css: styles.value
  }, (children == null ? void 0 : children.icon) && /*#__PURE__*/React.createElement(Icon, {
    as: children == null ? void 0 : children.icon,
    mr: "0.8em",
    sx: getIconStyles(children == null ? void 0 : children.icon)
  }), (children == null ? void 0 : children.label) || placeholder)), /*#__PURE__*/React.createElement(chakra.div, {
    __css: divStyles,
    hidden: hidden
  }, (children == null ? void 0 : children.icon) && /*#__PURE__*/React.createElement(Icon, {
    as: children == null ? void 0 : children.icon,
    mr: "0.8em",
    sx: getIconStyles(children == null ? void 0 : children.icon)
  }), /*#__PURE__*/React.createElement(chakra.input, _extends({
    __css: inputStyles,
    className: cx('chakra-select__select-input', props.className),
    placeholder: (placeholder == null ? void 0 : placeholder.label) || placeholder
  }, inputRest))));
});

if (__DEV__) {
  SelectInputValue.displayName = 'SelectInputValue';
}

var motionVariants = {
  enter: {
    visibility: 'visible',
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    transitionEnd: {
      visibility: 'hidden'
    },
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 0.1,
      easings: 'easeOut'
    }
  }
}; // @future: only call `motion(chakra.div)` when we drop framer-motion v3 support

var MotionDiv = 'custom' in motion ? motion.custom(chakra.div) : motion(chakra.div);
export var SelectList = forwardRef((props, ref) => {
  var _props$zIndex, _styles$list;

  var {
    rootProps
  } = props,
      rest = _objectWithoutPropertiesLoose(props, _excluded4);

  var {
    isOpen,
    onTransitionEnd,
    isLazy,
    listId
  } = useSelectContext();
  var renderOptions = !isLazy || isOpen;
  var listContext = useSelectList(rest, ref);

  var {
    handleOptionClick,
    options,
    width
  } = listContext,
      listProps = _objectWithoutPropertiesLoose(listContext, _excluded5);

  var positionerProps = useSelectPositioner(rootProps);
  var styles = useStyles();
  var {
    'data-popper-placement': placement
  } = positionerProps;
  var isTop = placement === 'top-start';

  var listStyles = _extends({
    width,
    outline: 0,
    borderRadius: isTop ? '10px 10px 0px 0px' : '0px 0px 10px 10px',
    borderWidth: isTop ? '1px' : '0px 1px 1px 1px'
  }, styles.list);

  return /*#__PURE__*/React.createElement(chakra.div, _extends({
    __css: {
      zIndex: (_props$zIndex = props.zIndex) != null ? _props$zIndex : (_styles$list = styles.list) == null ? void 0 : _styles$list.zIndex
    }
  }, positionerProps), /*#__PURE__*/React.createElement(MotionDiv, _extends({}, listProps, {
    /**
     * We could call this on either `onAnimationComplete` or `onUpdate`.
     * e-focusing works better with the `onUpdate` and `onAnimationComplete`
     * works better with event/state updates
     */
    __css: listStyles,
    animate: isOpen ? 'enter' : 'exit',
    className: cx('chakra-select__select-list', listContext.className),
    initial: false,
    onAnimationComplete: onTransitionEnd,
    variants: motionVariants
  }), /*#__PURE__*/React.createElement(chakra.div, {
    __css: styles.scroll
  }, renderOptions && options.map((option, index) => {
    var {
      value,
      label
    } = option;
    var id = listId + "-option-" + value;
    return /*#__PURE__*/React.createElement(SelectOption, {
      __css: styles.option,
      className: cx('chakra-select__select-option', listContext.className),
      "data-test-id": id,
      id: id + "-" + index,
      key: id + "-" + index,
      onClick: () => handleOptionClick(value),
      value: value
    }, label);
  }))));
});

if (__DEV__) {
  SelectList.displayName = 'SelectList';
}

export var SelectOption = forwardRef((props, ref) => {
  var rest = _extends({}, (_objectDestructuringEmpty(props), props));

  var styles = useStyles();
  var ctx = useSelectOption(rest, ref);
  return /*#__PURE__*/React.createElement(chakra.button, _extends({
    __css: styles.option,
    className: cx('chakra-select__select-option', ctx.className)
  }, ctx));
});

if (__DEV__) {
  SelectOption.displayName = 'SelectOption';
}
//# sourceMappingURL=select.js.map